import { useQuery } from "@tanstack/react-query";
import { UserSearch, ShieldCheck, ShieldAlert } from "lucide-react";

import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../../shared/api/hisIntegration";
import { patientsApi } from "../../../shared/api/patients";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * "Hồ sơ bệnh nhân trên HIS" — looks the patient up on the external HIS by CCCD (FIND_PATIENT,
 * ADR-0020) and shows the HIS-side identity: HIS patient code, name, DOB, BHYT card, address. Lets
 * staff see whether the patient already exists on the HIS (and pull their HIS code / BHYT) without
 * auto-merging anything into the Addy record. Shown only for a HIS-bound tenant with a CCCD.
 */
export function HisPatientLookupCard({
  patientId,
  patientCode,
  hasHis,
}: {
  patientId: string;
  patientCode: string | null | undefined;
  hasHis: boolean;
}) {
  const identifiers = useQuery({
    queryKey: ["patient", patientId, "identifiers-for-his"],
    queryFn: () => patientsApi.listIdentifiers(patientId),
    enabled: hasHis && !!patientId,
    staleTime: 5 * 60 * 1000,
  });
  const cccd =
    (identifiers.data ?? [])
      .filter((x) => x.identifierType === "CCCD")
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))[0]?.value ?? null;

  const key = cccd || patientCode || "";
  const q = useQuery({
    queryKey: hisIntegrationQueryKeys.patientLookup(key),
    queryFn: () => hisIntegrationApi.patientLookup({ socialId: cccd ?? undefined }),
    enabled: hasHis && !!cccd,
    staleTime: 60_000,
  });

  const rec = q.data;
  // Once we have the HIS patient code, check the BHYT card (validity window + registration place).
  const bhyt = useQuery({
    queryKey: hisIntegrationQueryKeys.bhytCheck(rec?.hisPatientCode ?? ""),
    queryFn: () => hisIntegrationApi.bhytCheck(rec!.hisPatientCode!),
    enabled: !!rec?.hisPatientCode,
    staleTime: 60_000,
  });

  if (!hasHis || !cccd) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <UserSearch aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        <CardTitle>Hồ sơ bệnh nhân trên HIS</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading && <p className="m-0 text-sm text-muted-foreground">Đang tra cứu trên HIS…</p>}
        {q.isError && (
          <p className="m-0 text-sm text-destructive">Không tra cứu được bệnh nhân trên HIS.</p>
        )}
        {!q.isLoading && !q.isError && !rec && (
          <p className="m-0 text-sm text-muted-foreground">
            Bệnh nhân chưa có hồ sơ trên HIS (theo CCCD {cccd}).
          </p>
        )}
        {rec && (
          <div className="grid gap-2.5">
            <Row label="Tên trên HIS">{rec.fullName ?? "—"}</Row>
            <Row label="Mã BN (HIS)" emphasis>
              {rec.hisPatientCode ?? "—"}
            </Row>
            <Row label="Năm sinh">{rec.birthYear ?? "—"}</Row>
            <Row label="Số điện thoại">{rec.mobile ?? "—"}</Row>
            <Row label="Thẻ BHYT">
              {rec.bhytCode ? (
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck aria-hidden="true" className="h-3.5 w-3.5 text-emerald-600" />
                  {rec.bhytCode}
                </span>
              ) : (
                <Badge variant="muted">Không có</Badge>
              )}
            </Row>
            <Row label="Địa chỉ">
              {[rec.address, rec.wardName, rec.provinceName].filter(Boolean).join(", ") || "—"}
            </Row>

            {/* BHYT card check from the HIS — validity window + registration place. */}
            {bhyt.data && (
              <div
                data-testid="his-bhyt-status"
                className={`mt-1 rounded-md border p-3 text-sm ${
                  bhyt.data.valid
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-amber-200 bg-amber-50"
                }`}
              >
                <div className="flex items-center gap-1.5 font-medium">
                  {bhyt.data.valid ? (
                    <ShieldCheck aria-hidden="true" className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <ShieldAlert aria-hidden="true" className="h-4 w-4 text-amber-600" />
                  )}
                  {bhyt.data.valid ? "Thẻ BHYT còn hiệu lực" : "Thẻ BHYT không hợp lệ / hết hạn"}
                </div>
                {(bhyt.data.validFrom || bhyt.data.validTo) && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Hạn thẻ: {bhyt.data.validFrom ?? "—"} → {bhyt.data.validTo ?? "—"}
                  </div>
                )}
                {bhyt.data.registrationPlaceCode && (
                  <div className="text-xs text-muted-foreground">
                    Nơi ĐKBĐ: {bhyt.data.registrationPlaceCode}
                  </div>
                )}
                {bhyt.data.note && (
                  <div className="text-xs text-muted-foreground">{bhyt.data.note}</div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  children,
  emphasis = false,
}: {
  label: string;
  children: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={
          emphasis
            ? "text-right font-semibold text-foreground"
            : "text-right font-medium text-foreground"
        }
      >
        {children}
      </span>
    </div>
  );
}
